// src/rms/pages/Pricing.js
// ============================================================================
// Pricing management — rate rules, add-ons, usage fees, cancellation terms
// ============================================================================
// All four are operator-owned data seeded from the vertical at provisioning.
// Nothing here is vertical-specific: a storage operator edits "First month half
// price" in the same screen a construction yard edits "Weekly discount".
// ============================================================================

import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import db from "../data";
import { useRms } from "../context/RmsContext";
import { useCollection } from "../hooks/useCollection";
import { PageHeader, StatusPill, Modal, Field, TextInput, Select, Banner, EmptyState } from "../components/ui";
import { optionLabelFor } from "../i18n/optionLabel";
import adEngine from "../ads/adEngine";
import { getLocale } from "../i18n/locale";
import i18n from "../i18n";

/**
 * Short weekday names, Monday first, in the reader's language.
 *
 * Deliberately not a catalogue key per day. Weekday names are not UI copy —
 * every locale already has them, correctly abbreviated and correctly cased,
 * and asking thirteen translators to retype them would only introduce ways to
 * get them wrong. 2024-01-01 was a Monday, which is what fixes the order.
 */
function weekdayNames() {
  const fmt = new Intl.DateTimeFormat(getLocale(), { weekday: "short" });
  return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(Date.UTC(2024, 0, 1 + i))));
}

const TABS = [
  { key: "rules", label: "Rate rules", labelKey: "pricing.tab.rules" },
  { key: "addons", label: "Add-ons & fees", labelKey: "pricing.tab.addons" },
  { key: "usage", label: "Usage fees", labelKey: "pricing.tab.usage" },
  { key: "cancellation", label: "Cancellation", labelKey: "pricing.tab.cancellation" },
];

const RULE_CATEGORIES = [
  { value: "seasonal", label: "Season / dates — adjust price for a period or weekday", labelKey: "opt.ruleCategory.seasonal" },
  { value: "length_of_rental", label: "Length of rental — tiered discount for longer bookings", labelKey: "opt.ruleCategory.lengthOfRental" },
  { value: "minimum_duration", label: "Minimum duration — enforce a minimum, no price change", labelKey: "opt.ruleCategory.minimumDuration" },
  { value: "search_window", label: "Search promotion — discount by when they book", labelKey: "opt.ruleCategory.searchWindow" },
  { value: "utilization", label: "Utilisation — surge when the fleet is busy", labelKey: "opt.ruleCategory.utilization" },
];

const CHARGE_TYPES = [
  { value: "once", label: "Once per booking", labelKey: "opt.chargeType.once" },
  { value: "per_day", label: "Per day", labelKey: "opt.chargeType.perDay" },
  { value: "per_unit", label: "Per unit", labelKey: "opt.chargeType.perUnit" },
  { value: "per_day_per_unit", label: "Per unit, per day", labelKey: "opt.chargeType.perDayPerUnit" },
];

function Pricing() {
  const { tab = "rules" } = useParams();
  const navigate = useNavigate();
  const { t } = useRms();
  const active = TABS.some((x) => x.key === tab) ? tab : "rules";

  return (
    <>
      <PageHeader
        title={t("pricing.pricing", "Pricing")}
        subtitle={t("pricing.rulesAddOnsAnd", "Rules, add-ons and fees. Seeded from your business type — all of it yours to change.")}
      />

      <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
        {TABS.map((x) => (
          <button
            key={x.key}
            type="button"
            className={`rms-btn ${active === x.key ? "rms-btn--primary" : "rms-btn--ghost"} rms-btn--sm`}
            onClick={() => navigate(`/rms/pricing/${x.key}`)}
          >
            {optionLabelFor(x)}
          </button>
        ))}
      </div>

      {active === "rules" ? <RulesTab t={t} /> : null}
      {active === "addons" ? <AddonsTab t={t} /> : null}
      {active === "usage" ? <UsageTab /> : null}
      {active === "cancellation" ? <CancellationTab /> : null}
    </>
  );
}

// ----------------------------------------------------------------------------

function RulesTab({ t }) {
  const { operator, invalidate } = useRms();
  const { rows: rules } = useCollection("rateRules", {}, { orderBy: "priority" });
  const { rows: assets } = useCollection("assets");
  const [editing, setEditing] = useState(null);

  const categories = useMemo(
    () => Array.from(new Set(assets.map((a) => a.category).filter(Boolean))),
    [assets]
  );

  const save = async (draft) => {
    adEngine.recordAction();
    const payload = {
      operator_id: operator.id,
      name: draft.name,
      category: draft.category,
      scope_type: draft.scope_type,
      scope_values: draft.scope_values,
      travel_starts_on: draft.travel_starts_on || null,
      travel_ends_on: draft.travel_ends_on || null,
      search_starts_on: draft.search_starts_on || null,
      search_ends_on: draft.search_ends_on || null,
      days_of_week: draft.days_of_week,
      adjustment_direction: draft.adjustment_direction || null,
      adjustment_kind: draft.adjustment_kind || null,
      adjustment_value: draft.adjustment_value === "" ? null : Number(draft.adjustment_value),
      tiers: draft.tiers,
      min_duration_units: draft.min_duration_units ? Number(draft.min_duration_units) : null,
      utilization_min_pct: draft.utilization_min_pct ? Number(draft.utilization_min_pct) : null,
      priority: Number(draft.priority) || 100,
      is_active: draft.is_active,
    };
    if (draft.id) await db.update("rateRules", draft.id, payload);
    else await db.create("rateRules", payload);
    setEditing(null);
    invalidate();
  };

  const toggle = async (rule) => {
    await db.update("rateRules", rule.id, { is_active: !rule.is_active });
    invalidate();
  };

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
        <button type="button" className="rms-btn rms-btn--primary" onClick={() => setEditing(blankRule())}>
          {t("pricing.newRule", "New rule")}
        </button>
      </div>

      {rules.length === 0 ? (
        <EmptyState
          title={t("pricing.rule.none", "No rate rules")}
          message={t("pricing.rulesAdjustYourBase", "Rules adjust your base rates by season, length of rental, or when someone books.")}
          action={
            <button type="button" className="rms-btn rms-btn--primary" onClick={() => setEditing(blankRule())}>
              {t("pricing.newRule", "New rule")}
            </button>
          }
        />
      ) : (
        <div className="rms-table-wrap">
          <table className="rms-table">
            <thead>
              <tr>
                <th>{t("pricing.rule", "Rule")}</th>
                <th>{t("availability.type", "Type")}</th>
                <th>{t("accessLog.when", "When")}</th>
                <th>{t("pricing.effect", "Effect")}</th>
                <th>{t("pricing.appliesTo", "Applies to")}</th>
                <th>{t("pricing.active", "Active")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id}>
                  <td><strong>{r.name}</strong></td>
                  <td style={{ color: "var(--rms-muted)", fontSize: 12.5 }}>{r.category.replace(/_/g, " ")}</td>
                  <td style={{ fontSize: 12.5 }}>{describeWhen(r)}</td>
                  <td style={{ fontSize: 12.5 }}>{describeEffect(r)}</td>
                  <td style={{ fontSize: 12.5, color: "var(--rms-muted)" }}>
                    {r.scope_type === "all" ? "All" : t("pricing.scopeCount", "{count, plural, one {# {scope}} other {# {scope}s}}", {
        count: r.scope_values?.length || 0,
        scope: r.scope_type,
      })}
                  </td>
                  <td>
                    <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={() => toggle(r)}>
                      <StatusPill status={r.is_active ? "active" : "cancelled"} label={r.is_active ? "on" : "off"} />
                    </button>
                  </td>
                  <td className="rms-num">
                    <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={() => setEditing(toRuleDraft(r))}>
                      {t("action.edit", "Edit")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing ? (
        <RuleForm draft={editing} categories={categories} t={t} onCancel={() => setEditing(null)} onSave={save} />
      ) : null}
    </>
  );
}

function RuleForm({ draft: initial, categories, t, onCancel, onSave }) {
  const { tLower } = useRms();
  const [draft, setDraft] = useState(initial);
  const [error, setError] = useState(null);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const isTiered = draft.category === "length_of_rental";
  const isMinimum = draft.category === "minimum_duration";
  const isSearch = draft.category === "search_window";
  const isUtilization = draft.category === "utilization";

  const submit = () => {
    if (!draft.name.trim()) return setError("Give the rule a name.");
    if (isMinimum && !draft.min_duration_units) return setError("Set the minimum number of units.");
    if (!isMinimum && !isTiered && !draft.adjustment_value) return setError("Set an adjustment amount.");
    setError(null);
    onSave(draft);
  };

  const toggleDay = (n) =>
    set("days_of_week", draft.days_of_week.includes(n) ? draft.days_of_week.filter((d) => d !== n) : [...draft.days_of_week, n]);

  return (
    <Modal
      title={draft.id ? t("pricing.rule.edit", "Edit {name}", { name: draft.name }) : t("pricing.rule.new", "New rate rule")}
      onClose={onCancel}
      width={700}
      footer={
        <>
          <button type="button" className="rms-btn rms-btn--ghost" onClick={onCancel}>{t("action.cancel", "Cancel")}</button>
          <button type="button" className="rms-btn rms-btn--primary" onClick={submit}>{t("pricing.rule.save", "Save rule")}</button>
        </>
      }
    >
      {error ? <Banner tone="danger">{error}</Banner> : null}

      <div className="rms-field-grid">
        <Field label={t("pricing.rule.name", "Rule name")} required>
          <TextInput value={draft.name} onChange={(v) => set("name", v)} placeholder={t("pricing.rule.namePlaceholder", "Summer peak")} />
        </Field>
        <Field label={t("pricing.priority", "Priority")} help={t("pricing.rule.priorityHelp", "Lower numbers apply first.")}>
          <input className="rms-input" type="number" value={draft.priority} onChange={(e) => set("priority", e.target.value)} />
        </Field>
      </div>

      <Field label={t("pricing.rule.type", "Rule type")} required>
        <Select value={draft.category} onChange={(v) => set("category", v)} options={RULE_CATEGORIES} placeholder="Choose…" />
      </Field>

      {!isSearch ? (
        <div className="rms-field-grid">
          <Field label={t("pricing.rule.travelFrom", "Travel from")} help={t("pricing.rule.travelHelp", "MM-DD repeats every year.")}>
            <TextInput value={draft.travel_starts_on} onChange={(v) => set("travel_starts_on", v)} placeholder="06-15" />
          </Field>
          <Field label={t("pricing.rule.travelTo", "Travel to")}>
            <TextInput value={draft.travel_ends_on} onChange={(v) => set("travel_ends_on", v)} placeholder="09-05" />
          </Field>
        </div>
      ) : (
        <>
          <Banner tone="info">
            {t("pricing.aSearchPromotionKeys", "A search promotion keys off")} <strong>when the customer books</strong>, not when they
            travel — a Black Friday offer for any future dates.
          </Banner>
          <div className="rms-field-grid">
            <Field label={t("pricing.rule.bookFrom", "Booking window from")}>
              <TextInput value={draft.search_starts_on} onChange={(v) => set("search_starts_on", v)} placeholder="11-25" />
            </Field>
            <Field label={t("pricing.rule.bookTo", "Booking window to")}>
              <TextInput value={draft.search_ends_on} onChange={(v) => set("search_ends_on", v)} placeholder="12-02" />
            </Field>
          </div>
        </>
      )}

      <div className="rms-field">
        <label className="rms-label">{t("pricing.rule.onlyDays", "Only on these days")}</label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {weekdayNames().map((d, i) => (
            <button
              key={d}
              type="button"
              className={`rms-btn rms-btn--sm ${draft.days_of_week.includes(i + 1) ? "rms-btn--primary" : "rms-btn--ghost"}`}
              onClick={() => toggleDay(i + 1)}
            >
              {d}
            </button>
          ))}
        </div>
        <div className="rms-help">{t("pricing.rule.daysHelp", "Leave all off to apply every day.")}</div>
      </div>

      {isMinimum ? (
        <Field label={t("pricing.rule.minUnits", "Minimum units")} required help={t("pricing.rule.minUnitsHelp", "Bookings shorter than this are rejected for these dates.")}>
          <input className="rms-input" type="number" min="1" value={draft.min_duration_units} onChange={(e) => set("min_duration_units", e.target.value)} />
        </Field>
      ) : isTiered ? (
        <TierEditor tiers={draft.tiers} onChange={(v) => set("tiers", v)} />
      ) : (
        <div className="rms-field-grid">
          <Field label={t("pricing.direction", "Direction")}>
            <Select
              value={draft.adjustment_direction}
              onChange={(v) => set("adjustment_direction", v)}
              placeholder={t("pricing.increase", "Increase")}
              options={[
                { value: "increase", label: "Increase price", labelKey: "opt.adjustDirection.increase" },
                { value: "decrease", label: "Decrease price", labelKey: "opt.adjustDirection.decrease" },
              ]}
            />
          </Field>
          <Field label={t("pricing.rule.amountType", "Amount type")}>
            <Select
              value={draft.adjustment_kind}
              onChange={(v) => set("adjustment_kind", v)}
              placeholder={t("pricing.percent", "Percent")}
              options={[
                { value: "percent", label: "Percent (%)", labelKey: "opt.adjustKind.percent" },
                { value: "flat", label: "Flat amount", labelKey: "opt.adjustKind.flat" },
                { value: "set", label: "Set price to", labelKey: "opt.adjustKind.set" },
              ]}
            />
          </Field>
          <Field label={t("pricing.value", "Value")} required>
            <input className="rms-input" type="number" step="0.01" value={draft.adjustment_value} onChange={(e) => set("adjustment_value", e.target.value)} />
          </Field>
        </div>
      )}

      {isUtilization ? (
        <Field label="Applies when fleet utilisation is at least (%)" help={t("pricing.rule.utilHelp", "Surge pricing when most of the fleet is already out.")}>
          <input className="rms-input" type="number" min="0" max="100" value={draft.utilization_min_pct} onChange={(e) => set("utilization_min_pct", e.target.value)} />
        </Field>
      ) : null}

      <div className="rms-field-grid">
        <Field label={t("pricing.appliesTo", "Applies to")}>
          <Select
            value={draft.scope_type}
            onChange={(v) => set("scope_type", v)}
            placeholder={t("pricing.all", "All")}
            options={[
              { value: "all", label: t("pricing.allAssets", "All {items}", { items: tLower("assetPlural", "assets") }) },
              { value: "category", label: "A category" },
            ]}
          />
        </Field>
        {draft.scope_type === "category" ? (
          <Field label={t("pricing.category", "Category")}>
            <Select
              value={draft.scope_values[0] || ""}
              onChange={(v) => set("scope_values", v ? [v] : [])}
              options={categories}
              placeholder="Choose…"
            />
          </Field>
        ) : null}
      </div>

      <label className="rms-checkline">
        <input type="checkbox" checked={draft.is_active} onChange={(e) => set("is_active", e.target.checked)} />
        {t("pricing.ruleIsActive", "Rule is active")}
      </label>
    </Modal>
  );
}

function TierEditor({ tiers, onChange }) {
  const { t } = useRms();
  const update = (i, key, value) => {
    const next = tiers.map((t, idx) => (idx === i ? { ...t, [key]: value } : t));
    onChange(next);
  };

  return (
    <div className="rms-field">
      <label className="rms-label">{t("pricing.rule.ladder", "Discount ladder")}</label>
      <div className="rms-help" style={{ marginBottom: 8 }}>
        The highest matching tier wins — a 10-day booking uses the 7-day tier unless a 10+ tier exists.
      </div>
      {tiers.map((tier, i) => (
        <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13 }}>{t("availability.from", "From")}</span>
          <input className="rms-input" style={{ width: 80 }} type="number" min="1" value={tier.minUnits ?? ""} onChange={(e) => update(i, "minUnits", Number(e.target.value))} />
          <span style={{ fontSize: 13 }}>units,</span>
          <select className="rms-select" style={{ width: 120 }} value={tier.direction || "decrease"} onChange={(e) => update(i, "direction", e.target.value)}>
            <option value="decrease">decrease</option>
            <option value="increase">increase</option>
          </select>
          <span style={{ fontSize: 13 }}>by</span>
          <input className="rms-input" style={{ width: 80 }} type="number" value={tier.value ?? ""} onChange={(e) => update(i, "value", Number(e.target.value))} />
          <select className="rms-select" style={{ width: 90 }} value={tier.kind || "percent"} onChange={(e) => update(i, "kind", e.target.value)}>
            <option value="percent">%</option>
            <option value="flat">flat</option>
          </select>
          <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={() => onChange(tiers.filter((_, idx) => idx !== i))}>
            {t("campaignForm.remove", "Remove")}
          </button>
        </div>
      ))}
      <button
        type="button"
        className="rms-btn rms-btn--ghost rms-btn--sm"
        onClick={() => onChange([...tiers, { minUnits: 7, direction: "decrease", kind: "percent", value: 10 }])}
      >
        + Add tier
      </button>
    </div>
  );
}

// ----------------------------------------------------------------------------

function AddonsTab({ t }) {
  const { operator, formatMoney, invalidate } = useRms();
  const { rows: addons } = useCollection("addons", {}, { orderBy: "position" });
  const [editing, setEditing] = useState(null);

  const save = async (draft) => {
    adEngine.recordAction();
    const payload = {
      operator_id: operator.id,
      name: draft.name,
      description: draft.description || null,
      price_cents: Math.round(Number(draft.price || 0) * 100),
      charge_type: draft.charge_type,
      is_required: draft.is_required,
      is_hidden: draft.is_hidden,
      taxable: draft.taxable,
      max_quantity: draft.max_quantity ? Number(draft.max_quantity) : null,
      default_quantity: 1,
      position: Number(draft.position) || 0,
      is_active: draft.is_active,
    };
    if (draft.id) await db.update("addons", draft.id, payload);
    else await db.create("addons", payload);
    setEditing(null);
    invalidate();
  };

  return (
    <>
      <Banner tone="info">
        <div>
          <strong>{t("pricing.required", "Required")}</strong> add-ons are applied automatically and can be removed case by
          case. <strong>{t("pricing.hidden", "Hidden")}</strong> ones never appear to customers — staff add them when needed,
          which is how cleaning and damage charges usually work.
        </div>
      </Banner>

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
        <button type="button" className="rms-btn rms-btn--primary" onClick={() => setEditing(blankAddon(addons.length))}>
          {t("pricing.addon.new", "New add-on")}
        </button>
      </div>

      {addons.length === 0 ? (
        <EmptyState
          title={t("pricing.addon.none", "No add-ons yet")}
          message={t("pricing.addon.noneBody", "Add-ons are optional or required extras billed on a booking.")}
        />
      ) : (
        <div className="rms-table-wrap">
          <table className="rms-table">
            <thead>
              <tr>
                <th>{t("pricing.addonColumn", "Add-on")}</th>
                <th className="rms-num">{t("pricing.price", "Price")}</th>
                <th>{t("inventory.charged", "Charged")}</th>
                <th>{t("pricing.flags", "Flags")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {addons.map((a) => (
                <tr key={a.id}>
                  <td>
                    <strong>{a.name}</strong>
                    {a.description ? <div style={{ fontSize: 12, color: "var(--rms-muted)" }}>{a.description}</div> : null}
                  </td>
                  <td className="rms-num">{formatMoney(a.price_cents)}</td>
                  <td style={{ fontSize: 12.5, color: "var(--rms-muted)" }}>{a.charge_type.replace(/_/g, " ")}</td>
                  <td style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {a.is_required ? <StatusPill status="active" label="required" /> : null}
                    {a.is_hidden ? <StatusPill status="pending" label="staff only" /> : null}
                    {!a.is_active ? <StatusPill status="cancelled" label="off" /> : null}
                  </td>
                  <td className="rms-num">
                    <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={() => setEditing(toAddonDraft(a))}>
                      {t("action.edit", "Edit")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing ? (
        <Modal
          title={editing.id ? t("pricing.addon.edit", "Edit {name}", { name: editing.name }) : t("pricing.addon.new", "New add-on")}
          onClose={() => setEditing(null)}
          footer={
            <>
              <button type="button" className="rms-btn rms-btn--ghost" onClick={() => setEditing(null)}>{t("action.cancel", "Cancel")}</button>
              <button type="button" className="rms-btn rms-btn--primary" onClick={() => save(editing)}>{t("action.save", "Save")}</button>
            </>
          }
        >
          <div className="rms-field-grid">
            <Field label={t("pricing.name", "Name")} required>
              <TextInput value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
            </Field>
            <Field label={`Price (${operator.currency})`} required>
              <input className="rms-input" type="number" min="0" step="0.01" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} />
            </Field>
            <Field label={t("pricing.charged", "Charged")}>
              <Select value={editing.charge_type} onChange={(v) => setEditing({ ...editing, charge_type: v })} options={CHARGE_TYPES} placeholder={t("pricing.once", "Once")} />
            </Field>
            <Field label={t("pricing.addon.maxQty", "Max quantity")} help={t("pricing.addon.maxQtyHelp", "Leave blank for unlimited.")}>
              <input className="rms-input" type="number" min="1" value={editing.max_quantity} onChange={(e) => setEditing({ ...editing, max_quantity: e.target.value })} />
            </Field>
          </div>
          <Field label={t("pricing.description", "Description")}>
            <TextInput value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} />
          </Field>
          <label className="rms-checkline">
            <input type="checkbox" checked={editing.is_required} onChange={(e) => setEditing({ ...editing, is_required: e.target.checked })} />
            {t("pricing.requiredAppliedAutomaticallyTo", "Required — applied automatically to every booking")}
          </label>
          <label className="rms-checkline">
            <input type="checkbox" checked={editing.is_hidden} onChange={(e) => setEditing({ ...editing, is_hidden: e.target.checked })} />
            {t("pricing.hiddenFromCustomersStaff", "Hidden from customers — staff can still add it")}
          </label>
          <label className="rms-checkline">
            <input type="checkbox" checked={editing.taxable} onChange={(e) => setEditing({ ...editing, taxable: e.target.checked })} />
            {t("pricing.taxable", "Taxable")}
          </label>
          <label className="rms-checkline">
            <input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
            {t("pricing.active", "Active")}
          </label>
        </Modal>
      ) : null}
    </>
  );
}

// ----------------------------------------------------------------------------

function UsageTab() {
  const { t } = useRms();
  const { config, formatMoney } = useRms();
  const { rows: schedules } = useCollection("usageFeeSchedules");

  if (!config.usageMetrics?.length) {
    return (
      <EmptyState
        title={t("pricing.usage.none", "This business type doesn't meter usage")}
        message={t("pricing.usageFeesApplyTo", "Usage fees apply to verticals that track mileage, engine hours or similar. Yours tracks condition and access instead.")}
      />
    );
  }

  return (
    <>
      <Banner tone="info">
        {t(
          "pricing.allowancesPer",
          "Allowances are per {metrics}. Anything beyond the included amount is billed automatically at check-in.",
          { metrics: config.usageMetrics.map((m) => m.label.toLowerCase()).join(" and ") }
        )}
      </Banner>

      <div className="rms-table-wrap">
        <table className="rms-table">
          <thead>
            <tr>
              <th>{t("pricing.schedule", "Schedule")}</th>
              <th>{t("pricing.metric", "Metric")}</th>
              <th className="rms-num">{t("pricing.included", "Included")}</th>
              <th className="rms-num">{t("pricing.overage", "Overage")}</th>
              <th>{t("pricing.active", "Active")}</th>
            </tr>
          </thead>
          <tbody>
            {schedules.map((s) => (
              <tr key={s.id}>
                <td><strong>{s.name}</strong></td>
                <td style={{ color: "var(--rms-muted)", fontSize: 12.5 }}>{s.metric_key.replace(/_/g, " ")}</td>
                <td className="rms-num">
                  {Number(s.included_per_period).toLocaleString(getLocale())} {s.unit} / {s.period}
                </td>
                <td className="rms-num">
                  {s.flat_cents_per_unit ? `${formatMoney(s.flat_cents_per_unit)} per ${s.unit}` : "included"}
                </td>
                <td><StatusPill status={s.is_active ? "active" : "cancelled"} label={s.is_active ? "on" : "off"} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function CancellationTab() {
  const { t } = useRms();
  const { rows: policies } = useCollection("cancellationPolicies");

  return (
    <>
      <Banner tone="info">
        {t("pricing.laddersAreReadMost", "Ladders are read most-generous-first. Anything later than the last tier refunds nothing.")}
      </Banner>
      {policies.map((p) => (
        <div key={p.id} className="rms-card rms-card-pad" style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 className="rms-section-title" style={{ margin: 0 }}>{p.name}</h3>
            {p.is_default ? <StatusPill status="active" label="default" /> : null}
          </div>
          <p style={{ fontSize: 13.5, color: "var(--rms-muted-strong)", margin: "6px 0 10px" }}>{p.description}</p>
          <ul style={{ margin: 0, paddingInlineStart: 18, fontSize: 13.5 }}>
            {(p.tiers || []).map((tier, i) => (
              <li key={i}>
                {t("pricing.cancelTier", "Cancel {days}+ days before pickup — {percent}% refund", {
                  days: tier.daysBefore ?? tier.days_before,
                  percent: tier.refundPct ?? tier.refund_pct,
                })}
              </li>
            ))}
            <li style={{ color: "var(--rms-muted)" }}>{t("pricing.laterThanThatNo", "Later than that — no refund")}</li>
          </ul>
        </div>
      ))}
    </>
  );
}

// ----------------------------------------------------------------------------

function describeWhen(r) {
  const bits = [];
  if (r.travel_starts_on || r.travel_ends_on) bits.push(`${r.travel_starts_on || "…"} → ${r.travel_ends_on || "…"}`);
  if (r.search_starts_on) bits.push(`booked ${r.search_starts_on} → ${r.search_ends_on || "…"}`);
  if (r.days_of_week?.length) {
    const names = weekdayNames();
    bits.push(r.days_of_week.map((d) => names[d - 1]).join(", "));
  }
  if (r.utilization_min_pct != null) bits.push(`≥${r.utilization_min_pct}% utilised`);
  return bits.join(" · ") || i18n.t("pricing.rule.always", { defaultValue: "Always" });
}

function describeEffect(r) {
  if (r.category === "minimum_duration") return `Minimum ${r.min_duration_units}`;
  if (r.tiers?.length) {
    return r.tiers
      .map((t) => `${t.minUnits}+ → ${t.direction === "decrease" ? "−" : "+"}${t.value}${t.kind === "percent" ? "%" : ""}`)
      .join(", ");
  }
  if (!r.adjustment_value) return "—";
  const sign = r.adjustment_direction === "decrease" ? "−" : "+";
  return `${sign}${r.adjustment_value}${r.adjustment_kind === "percent" ? "%" : ""}`;
}

function blankRule() {
  return {
    id: null, name: "", category: "seasonal", scope_type: "all", scope_values: [],
    travel_starts_on: "", travel_ends_on: "", search_starts_on: "", search_ends_on: "",
    days_of_week: [], adjustment_direction: "increase", adjustment_kind: "percent",
    adjustment_value: "", tiers: [], min_duration_units: "", utilization_min_pct: "",
    priority: 100, is_active: true,
  };
}

function toRuleDraft(r) {
  return {
    id: r.id, name: r.name, category: r.category, scope_type: r.scope_type,
    scope_values: r.scope_values || [],
    travel_starts_on: r.travel_starts_on || "", travel_ends_on: r.travel_ends_on || "",
    search_starts_on: r.search_starts_on || "", search_ends_on: r.search_ends_on || "",
    days_of_week: r.days_of_week || [],
    adjustment_direction: r.adjustment_direction || "increase",
    adjustment_kind: r.adjustment_kind || "percent",
    adjustment_value: r.adjustment_value ?? "",
    tiers: r.tiers || [],
    min_duration_units: r.min_duration_units ?? "",
    utilization_min_pct: r.utilization_min_pct ?? "",
    priority: r.priority ?? 100, is_active: r.is_active !== false,
  };
}

function blankAddon(position) {
  return {
    id: null, name: "", description: "", price: "", charge_type: "once",
    is_required: false, is_hidden: false, taxable: true, max_quantity: "",
    position, is_active: true,
  };
}

function toAddonDraft(a) {
  return {
    id: a.id, name: a.name, description: a.description || "",
    price: ((a.price_cents || 0) / 100).toString(), charge_type: a.charge_type,
    is_required: !!a.is_required, is_hidden: !!a.is_hidden, taxable: a.taxable !== false,
    max_quantity: a.max_quantity || "", position: a.position ?? 0, is_active: a.is_active !== false,
  };
}

export default Pricing;
